import { isPlatformBrowser } from "@angular/common";
import { Injectable, Inject, PLATFORM_ID } from "@angular/core";

@Injectable({
  providedIn: "root"
})
export class MentionProcessorService {
  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}
  /**
   * Processes HTML content to convert mention spans into clickable anchor tags
   * @param html The HTML content containing mention spans
   * @returns The processed HTML with mentions converted to links
   */
  processMentions(html: string): string {
    if (!html) {
      return html;
    }

    // Skip processing in SSR environment
    if (!isPlatformBrowser(this.platformId)) {
      return html;
    }

    // Create a temporary container to parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Find all mention spans
    const mentions = doc.querySelectorAll("span.mention[data-link]");

    mentions.forEach(mention => {
      const link = mention.getAttribute("data-link");
      const mentionText = mention.textContent || "";
      const denotationChar = mention.getAttribute("data-denotation-char");

      if (link) {
        // Handle image mentions differently
        if (denotationChar === "#") {
          const thumbnail = mention.getAttribute("data-thumbnail");
          const title = mention.getAttribute("data-value");
          const id = mention.getAttribute("data-id");

          // Create a clean semantic structure without inline styles
          const container = doc.createElement("span");
          container.className = "image-mention";
          container.setAttribute("data-id", id || "");
          container.setAttribute("data-value", title || "");
          container.setAttribute("data-thumbnail", thumbnail || "");
          container.setAttribute("data-link", link);

          // Create an anchor wrapper
          const anchor = doc.createElement("a");
          anchor.href = link;
          anchor.target = "_blank";
          anchor.className = "image-mention-link";

          // Add the image if thumbnail is available
          if (thumbnail) {
            const img = doc.createElement("img");
            img.src = thumbnail;
            img.alt = title || "";
            img.className = "image-mention-thumbnail";
            anchor.appendChild(img);
          }

          // Add the title
          const titleSpan = doc.createElement("span");
          titleSpan.className = "image-mention-title";
          titleSpan.textContent = title || "";
          anchor.appendChild(titleSpan);

          container.appendChild(anchor);

          // Replace the span with the container
          mention.parentNode?.replaceChild(container, mention);
        } else {
          // Handle user mentions (default behavior)
          const anchor = doc.createElement("a");
          anchor.href = link;
          anchor.className = "mention";
          anchor.textContent = mentionText;

          // Copy other data attributes if needed
          const dataId = mention.getAttribute("data-id");
          if (dataId) {
            anchor.setAttribute("data-id", dataId);
          }

          const dataValue = mention.getAttribute("data-value");
          if (dataValue) {
            anchor.setAttribute("data-value", dataValue);
          }

          // Replace the span with the anchor
          mention.parentNode?.replaceChild(anchor, mention);
        }
      }
    });

    // Return the processed HTML as string
    return doc.body.innerHTML;
  }

  /**
   * Fixes legacy user URLs by replacing /users/ with /u/
   * This is needed because some content has old-format URLs from the legacy website
   * @param html The HTML content containing legacy user URLs
   * @returns The HTML with user URLs normalized to /u/ format
   */
  fixLegacyUserUrls(html: string): string {
    if (!html) {
      return html;
    }

    // Simple regex replacement - very performant
    return html.replace(/href="\/users\//g, 'href="/u/');
  }
}
